import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useConfig } from "@/hooks/useConfig";
import { MapPin, ShieldAlert } from "lucide-react";

export default function CheckRestrictionPage() {
  const [pincode, setPincode] = useState("");
  const [state, setState] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const { getItems } = useConfig();
  const states = (getItems("indian_states") || []).map(s => s.value || s.key);

  const check = async () => {
    if (pincode && !/^\d{6}$/.test(pincode)) { toast.error("Pincode must be 6 digits"); return; }
    if (!pincode && !state) { toast.error("Enter a pincode or select a state"); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (pincode) params.set("pincode", pincode);
      if (state) params.set("state", state);
      const r = await api.get(`/brands/check-restriction?${params}`);
      setResults(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Check failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-lg mx-auto space-y-4" data-testid="check-restriction-page">
      <h1 className="text-xl font-bold text-zinc-900"><MapPin className="w-5 h-5 inline mr-1" /> Check Brand Restriction</h1>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-zinc-500">Pincode (6 digits)</Label>
          <Input className="h-8 text-xs mt-0.5" value={pincode} onChange={e => setPincode(e.target.value)} placeholder="e.g. 500081" data-testid="input-pincode" />
        </div>
        <div>
          <Label className="text-xs text-zinc-500">State (optional)</Label>
          <select className="w-full h-8 text-xs border rounded-md px-2 mt-0.5" value={state} onChange={e => setState(e.target.value)} data-testid="select-state">
            <option value="">Any</option>
            {states.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <Button onClick={check} disabled={loading} data-testid="check-btn">{loading ? "Checking..." : "Check"}</Button>
      {results && (
        <div data-testid="restriction-results">
          {results.blocked_brands?.length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-red-700"><ShieldAlert className="w-4 h-4 inline mr-1" />{results.blocked_brands.length} brand(s) blocked here</p>
              {results.blocked_brands.map((b, i) => (
                <div key={i} className="border border-red-200 bg-red-50 rounded-sm p-2 text-xs">
                  <span className="font-medium">{b.display_name}</span> — {b.reason}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-emerald-600">No brand is blocked here.</p>
          )}
        </div>
      )}
    </div>
  );
}
